import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes = [
  // Admin (must come before public routes)
  {
    path: '/admin',
    component: () => import('@/components/layout/AdminLayout.vue'),
    meta: { requiresAdmin: true },
    children: [
      { path: '',             name: 'AdminOverview',   component: () => import('@/views/admin/AdminOverviewView.vue') },
      { path: 'users',        name: 'AdminUsers',      component: () => import('@/views/admin/AdminUsersView.vue') },
      { path: 'withdrawals',  name: 'AdminWithdrawals',component: () => import('@/views/admin/AdminWithdrawalsView.vue') },
      { path: 'sender-ids',   name: 'AdminSenderIds',  component: () => import('@/views/admin/AdminSenderIdsView.vue') },
      { path: 'transactions', name: 'AdminTransactions',component: () => import('@/views/admin/AdminTransactionsView.vue') },
      { path: 'messages/sms',      name: 'AdminSmsMessages', component: () => import('@/views/admin/AdminMessagesView.vue') },
      { path: 'messages/whatsapp', name: 'AdminWaMessages',  component: () => import('@/views/admin/AdminMessagesView.vue') },
      { path: 'gateways',          name: 'AdminGateways',    component: () => import('@/views/admin/AdminGatewaysView.vue') },
      { path: 'pricing',           name: 'AdminPricing',     component: () => import('@/views/admin/AdminPricingView.vue') },
      { path: 'payments',          name: 'AdminPayments',    component: () => import('@/views/admin/AdminPaymentsView.vue') },
      { path: 'activity',          name: 'AdminActivity',    component: () => import('@/views/admin/AdminActivityView.vue') },
      { path: 'automation',        name: 'AdminAutomation',  component: () => import('@/views/admin/AdminAutomationView.vue') },
      { path: 'card-reports',      name: 'AdminCardReports', component: () => import('@/views/admin/AdminCardReportsView.vue') },
      { path: 'card-studio-reports', name: 'AdminCardStudioReports', component: () => import('@/views/admin/AdminCardStudioReportsView.vue') },
      { path: 'contact-messages',  name: 'AdminContactMessages', component: () => import('@/views/admin/AdminContactMessagesView.vue') },
      { path: 'site-settings',    name: 'AdminSiteSettings', component: () => import('@/views/admin/AdminSiteSettingsView.vue') },
      { path: 'wa-templates',     name: 'AdminWaTemplates',  component: () => import('@/views/admin/AdminWaTemplatesView.vue') },
      { path: 'profile',          name: 'AdminProfile',      component: () => import('@/views/admin/AdminProfileView.vue') },
      { path: 'referrals',        name: 'AdminReferrals',    component: () => import('@/views/admin/AdminReferralsView.vue') },
      { path: 'blog',             name: 'AdminBlog',         component: () => import('@/views/admin/AdminBlogView.vue') },
      { path: 'pages',            name: 'AdminPages',        component: () => import('@/views/admin/AdminPagesView.vue') },
      { path: 'admins',           name: 'AdminAdmins',       component: () => import('@/views/admin/AdminAdminsView.vue') },
    ]
  },

  // Payment callback (public — no auth needed, but verify.php checks JWT)
  {
    path: '/payment/callback',
    name: 'PaymentCallback',
    component: () => import('@/views/PaymentCallbackView.vue'),
    meta: { requiresAuth: true }
  },

  // Public website
  {
    path: '/',
    component: () => import('@/components/public/PublicLayout.vue'),
    children: [
      { path: '',             name: 'Home',       component: () => import('@/views/public/HomeView.vue') },
      { path: 'services',     name: 'Services',   component: () => import('@/views/public/ServicesView.vue') },
      { path: 'pricing',      name: 'Pricing',    component: () => import('@/views/public/PricingView.vue') },
      { path: 'how-it-works', name: 'HowItWorks', component: () => import('@/views/public/HowItWorksView.vue') },
      { path: 'about',        name: 'About',      component: () => import('@/views/public/AboutView.vue') },
      { path: 'contact',      name: 'Contact',    component: () => import('@/views/public/ContactView.vue') },
      { path: 'docs',         name: 'Docs',       component: () => import('@/views/public/DocsView.vue') },
      { path: 'blog',         name: 'Blog',       component: () => import('@/views/public/BlogView.vue') },
      { path: 'blog/:slug',   name: 'BlogPost',   component: () => import('@/views/public/BlogPostView.vue') },
      { path: 'privacy',      name: 'Privacy',    component: () => import('@/views/public/PageView.vue'), meta: { pageKey: 'privacy-policy' } },
      { path: 'terms',        name: 'Terms',      component: () => import('@/views/public/PageView.vue'), meta: { pageKey: 'terms-of-use' } },
      { path: 'cookies',      name: 'Cookies',    component: () => import('@/views/public/PageView.vue'), meta: { pageKey: 'cookie-policy' } },
      { path: 'ndpr',         name: 'Ndpr',       component: () => import('@/views/public/PageView.vue'), meta: { pageKey: 'ndpr-compliance' } },
      { path: 'spam-policy',  name: 'SpamPolicy',   component: () => import('@/views/public/PageView.vue'), meta: { pageKey: 'spam-policy' } },
      { path: 'refund-policy',name: 'RefundPolicy', component: () => import('@/views/public/PageView.vue'), meta: { pageKey: 'refund-policy' } },
      { path: 'referral-program', name: 'ReferralProgram', component: () => import('@/views/public/ReferralProgramView.vue') },
      { path: 'careers',      name: 'Careers',    component: () => import('@/views/public/CareersView.vue') },
      { path: 'press',        name: 'Press',      component: () => import('@/views/public/PressView.vue') },
      { path: 'status',       name: 'Status',     component: () => import('@/views/public/StatusView.vue') },
      { path: ':pathMatch(.*)*', name: 'NotFound', component: () => import('@/views/NotFoundView.vue') },
    ]
  },

  // Auth
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { guest: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { guest: true }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: () => import('@/views/auth/VerifyEmailView.vue'),
    meta: { guest: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { guest: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { guest: true }
  },

  // Authenticated app
  {
    path: '/app',
    component: () => import('@/components/layout/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/dashboard' },
      { path: '/dashboard',            name: 'Dashboard',   component: () => import('@/views/DashboardView.vue') },
      { path: '/wallet',               name: 'Wallet',      component: () => import('@/views/wallet/WalletView.vue') },
      { path: '/wallet/transactions',  name: 'Transactions',component: () => import('@/views/wallet/TransactionHistoryView.vue') },
      { path: '/scheduled',             name: 'Scheduled',   component: () => import('@/views/scheduled/ScheduledCampaignsView.vue') },
      { path: '/sms/send',             name: 'SmsSend',     component: () => import('@/views/sms/SendSmsView.vue') },
      { path: '/sms/reports',          name: 'SmsReports',  component: () => import('@/views/sms/SmsReportsView.vue') },
      { path: '/sms/groups',           name: 'SmsGroups',   component: () => import('@/views/sms/ContactGroupsView.vue') },
      { path: '/sms/sender-id',        name: 'SmsSenderId', component: () => import('@/views/sms/SenderIdView.vue') },
      { path: '/sms/templates',        name: 'SmsTemplates',component: () => import('@/views/sms/SmsTemplatesView.vue') },
      { path: '/whatsapp/send',        name: 'WASend',      component: () => import('@/views/whatsapp/SendWhatsAppView.vue') },
      { path: '/whatsapp/reports',     name: 'WAReports',   component: () => import('@/views/whatsapp/WhatsAppReportsView.vue') },
      { path: '/whatsapp/templates',   name: 'WATemplates', component: () => import('@/views/whatsapp/WhatsAppTemplatesView.vue') },
      { path: '/automation/birthday',  name: 'Birthday',    component: () => import('@/views/automation/BirthdayView.vue') },
      { path: '/automation/cards',     name: 'AutomationCards', component: () => import('@/views/automation/CardsView.vue') },
      { path: '/automation/card-studio', name: 'CardStudio', component: () => import('@/views/automation/CardStudioView.vue') },
      { path: '/referral',             name: 'Referral',    component: () => import('@/views/referral/ReferralView.vue') },
      { path: '/links',                name: 'Links',       component: () => import('@/views/links/LinksView.vue') },
      { path: '/ai/generate',          name: 'AiGenerator', component: () => import('@/views/ai/AiGeneratorView.vue') },
      { path: '/developer',            name: 'Developer',   component: () => import('@/views/developer/DeveloperView.vue') },
      { path: '/vtu',                  name: 'Vtu',         component: () => import('@/views/vtu/VtuView.vue') },
      { path: '/settings',             name: 'Settings',    component: () => import('@/views/settings/ProfileView.vue') },
    ]
  },

  { path: '/:pathMatch(.*)*', redirect: '/' }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach((to) => {
  const auth    = useAuthStore()
  const isAdmin = auth.user?.role === 'admin'
  const isAuth  = auth.isAuthenticated

  // Must be logged in
  if (to.meta.requiresAuth   && !isAuth) return { name: 'Login' }
  if (to.meta.requiresAdmin  && !isAuth) return { name: 'Login' }

  // Admin-only routes: block regular users
  if (to.meta.requiresAdmin && !isAdmin) return { name: 'Dashboard' }

  // Guest routes: redirect authenticated users
  if (to.meta.guest && isAuth) {
    return isAdmin ? { name: 'AdminOverview' } : { name: 'Dashboard' }
  }

  // Block admins from regular user dashboard — redirect to admin
  if (to.meta.requiresAuth && isAdmin && !to.meta.requiresAdmin) {
    return { name: 'AdminOverview' }
  }
})

// If the app was redeployed while a tab was open, old lazy-loaded chunk
// URLs 404 and navigation silently fails — recover with a full reload.
router.onError((error, to) => {
  if (/loading chunk \S* failed|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i.test(error.message)) {
    window.location.href = to?.fullPath ?? window.location.href
  }
})

export default router
